import { ChefHat, Key, Lock, Shield, ShoppingCart, User, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import logoImage from '../components/assets/battasesukhsagarlogo.png';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup, InputWithIcon } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import { media, theme } from '../styles/theme';
import { authenticateAdmin, authenticateUser } from '../utils/authData';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;

  ${media.down.sm} {
    padding: ${theme.spacing.md};
  }
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 450px;
  padding: ${theme.spacing['2xl']};
  position: relative;
  z-index: 1;

  ${media.down.sm} {
    padding: ${theme.spacing.xl};
    margin: ${theme.spacing.md};
    max-width: 100%;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  
  .logo-image {
    width: 120px;
    height: auto;
    margin: 0 auto ${theme.spacing.md};
    display: block;
    object-fit: contain;
  }
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.base};
  }

  ${media.down.sm} {
    margin-bottom: ${theme.spacing.lg};
    
    .logo-image {
      width: 100px;
    }
    
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const RoleSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};

  ${media.down.sm} {
    gap: ${theme.spacing.sm};
  }
`;

const RoleButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  border: 1px solid ${({ selected }) => selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${({ selected }) => selected ? `${theme.colors.primary}10` : theme.colors.white};
  color: ${({ selected }) => selected ? theme.colors.primary : theme.colors.text};
  cursor: pointer;
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${({ selected }) => selected ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}08;
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 24px;
    height: 24px;
    pointer-events: none;
  }

  span {
    font-size: ${theme.typography.fontSize.sm};
    pointer-events: none;
  }

  p {
    pointer-events: none;
  }

  ${media.down.sm} {
    padding: ${theme.spacing.sm};
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSize.sm};
  text-align: center;
  padding: ${theme.spacing.md};
  background: linear-gradient(135deg, ${theme.colors.error}10 0%, ${theme.colors.error}05 100%);
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  animation: shake 0.3s;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;


const PinInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const PinDots = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const PinDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${({ filled }) => filled ? theme.colors.primary : theme.colors.border};
  background-color: ${({ filled }) => filled ? theme.colors.primary : 'transparent'};
  transition: all 0.2s ease;
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  text-align: center;
  margin-top: ${theme.spacing.xs};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${theme.colors.white};
  border-radius: 50%;
  animation: ${rotate} 0.6s linear infinite;
  margin-right: ${theme.spacing.xs};
`;


const roles = [
  { id: 'admin', name: 'Admin', icon: Shield, description: '6-digit PIN' },
  { id: 'waiter', name: 'Waiter', icon: Users, description: 'User ID & Password' },
  { id: 'counter', name: 'Counter', icon: ShoppingCart, description: 'User ID & Password' },
  { id: 'kitchen', name: 'Kitchen', icon: ChefHat, description: 'User ID & Password' },
];

function Login() {
  const [selectedRole, setSelectedRole] = useState('admin');
  const [pin, setPin] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Reset form when role changes
  useEffect(() => {
    setPin('');
    setUserId('');
    setPassword('');
    setError('');
  }, [selectedRole]);

  const handlePinChange = (e) => {
    // Using replace with regex (replaceAll doesn't support regex patterns)
    const value = e.target.value.replace(/\D/g, ''); // Only numbers
    if (value.length <= 6) {
      setPin(value);
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      let authenticated = false;

      if (selectedRole === 'admin') {
        // Admin authentication with PIN
        if (!pin || pin.length !== 6) {
          setError('Please enter a valid 6-digit PIN');
          setIsLoading(false);
          return;
        }
        authenticated = authenticateAdmin(pin);
        if (!authenticated) {
          setError('Invalid PIN. Please try again.');
          setIsLoading(false);
          return;
        }
      } else {
        // Other roles authentication with User ID and Password
        if (!userId || !password) {
          setError('Please enter both User ID and Password');
          setIsLoading(false);
          return;
        }
        const user = authenticateUser(selectedRole, userId, password);
        if (!user) {
          setError('Invalid User ID or Password. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Navigate based on role
      switch (selectedRole) {
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'waiter':
          navigate('/waiter/ordering');
          break;
        case 'counter':
          navigate('/counter/billing');
          break;
        case 'kitchen':
          navigate('/kitchen/display');
          break;
        default:
          navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isAdmin = selectedRole === 'admin';

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <img src={logoImage} alt="Logo" className="logo-image" />
        </Logo>

        <RoleSelector>
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <RoleButton
                key={role.id}
                selected={selectedRole === role.id}
                onClick={() => setSelectedRole(role.id)}
                type="button"
              >
                <Icon />
                <span>{role.name}</span>
                <InfoText style={{ fontSize: '10px', margin: 0 }}>
                  {role.description}
                </InfoText>
              </RoleButton>
            );
          })}
        </RoleSelector>

        <Form onSubmit={handleLogin}>
          {error && (
            <ErrorMessage>
              <Lock size={16} />
              {error}
            </ErrorMessage>
          )}

          {isAdmin ? (
            <PinInputContainer>
              <InputGroup>
                <label style={{ textAlign: 'center', display: 'block' }}>
                  <Key size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Admin PIN
                </label>
                <PinDots>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <PinDot key={index} filled={pin.length > index} />
                  ))}
                </PinDots>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  placeholder="Enter 6-digit PIN"
                  value={pin}
                  onChange={handlePinChange}
                  isPin
                  autoFocus
                  style={{ textAlign: 'center' }}
                />
                <InfoText>Enter your 6-digit security PIN</InfoText>
              </InputGroup>
            </PinInputContainer>
          ) : (
            <>
              <InputGroup>
                <label>
                  <User size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  User ID
                </label>
                <InputWithIcon hasLeftIcon>
                  <User className="icon-left" size={20} />
                  <Input
                    type="text"
                    placeholder="Enter User ID"
                    value={userId}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      setError('');
                    }}
                    autoFocus
                  />
                </InputWithIcon>
              </InputGroup>

              <InputGroup>
                <label>
                  <Lock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Password
                </label>
                <InputWithIcon hasLeftIcon>
                  <Lock className="icon-left" size={20} />
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                  />
                </InputWithIcon>
              </InputGroup>
            </>
          )}

          <Button 
            variant="primary" 
            type="submit" 
            fullWidth 
            size="lg"
            disabled={isLoading || (isAdmin ? pin.length !== 6 : !userId || !password)}
          >
            {isLoading && <LoadingSpinner />}
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>

          {!isAdmin && (
            <InfoText>
              Demo Credentials: {selectedRole}001 / {selectedRole}123
            </InfoText>
          )}
        </Form>
      </LoginCard>
      <Footer hasSidebar={false} />
    </LoginContainer>
  );
}

export default Login;
